import { Button, Select, Spinner } from 'alurkerja-ui'
import { Fragment, createContext, useState } from 'react'
import { ChevronLeft, ChevronRight, RefreshCcw } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { Section } from '@/components/shared/section/Section'
import { CardTask } from '@/components/shared/card/CardTask'
import { Empty } from '@/pages/others/Empty'
import { TasklistService } from '@/api/tasklist'
import { IMyTask } from '@/utils/types/mytask'
import { TaskDetail } from '@/pages/tasklist/TaskDetail'
import { useNavigate, useSearchParams } from 'react-router-dom'

export type IMyTaskContext = {
  data: IMyTask
  resetSelectedTask?: () => void
  refetchMyTask?: () => void
  setActiveTaskId?: React.Dispatch<React.SetStateAction<string | null>>
}

export type IMyTaskContextNew = {
  data: string
  processDefinition: string
  resetSelectedTask?: () => void
  refetchMyTask?: () => Promise<unknown>
  setActiveTaskId?: React.Dispatch<React.SetStateAction<string | null>>
}

/**
 * Context for MyTask page
 *
 * This context is used to share data and functions between components in the MyTask page
 *
 * @property {IMyTask} data - The data of the task
 * @property {() => void} resetSelectedTask - Function to reset the selected task
 * @property {() => void} refetchMyTask - Function to refetch the tasks
 * @property {(id: string | null) => void} setActiveTaskId - Function to set the active task id
 */
export const MyTaskContext = createContext<IMyTaskContext>({
  data: {} as IMyTask,
})

/**
 * Context for MyTask page
 *
 * This context is used to share data and functions between components in the MyTask page
 *
 * @property {string} data - The data of the task
 * @property {string} processDefinition - The process definition of the task
 * @property {() => void} resetSelectedTask - Function to reset the selected task
 * @property {() => void} refetchMyTask - Function to refetch the tasks
 * @property {(id: string | null) => void} setActiveTaskId - Function to set the active task id
 */
export const MyTaskContextNew = createContext<IMyTaskContextNew>({
  data: '',
  processDefinition: '',
})

export const MyTask = () => {
  // get params ?processKey from url using react-router-dom
  const [searchParams] = useSearchParams()
  const processKey = searchParams.get('processKey')
  const page = searchParams.get('page') ?? '0'
  const navigate = useNavigate()

  /**
   * Fetch all tasks for the user
   * If processKey is provided, fetch tasks for that business process
   * If processKey is not provided, fetch all tasks
   */
  const {
    data: tasks,
    isFetching,
    refetch,
  } = TasklistService.useGetMyTasks({ processKey: processKey ?? '', page: page ?? '0' })

  /**
   * Fetch all business processes
   */
  const { data: businessProcesses, isFetching: isFetchingBusinessProcesses } = TasklistService.useGetProcesses()

  /**
   * Options for select field to filter by business process
   * First option is "All Processes" which will fetch all tasks
   * Other options are from the list of business processes
   * Each option has `label` which is the name of the business process
   * and `value` which is the key of the business process
   */
  const businessProcessesOptions: { label: string; value: string }[] = [
    {
      label: 'All Processes',
      value: 'all',
    },
    ...(businessProcesses?.map(businessProcess => ({
      label: businessProcess.name,
      value: businessProcess.key,
    })) || []),
  ]

  const selectedBusinessProcess = businessProcessesOptions.find(businessProcess => businessProcess.value === processKey)

  /**
   * @description
   * Mutation to fetch task data
   * @param {object} selected - Selected task data
   * @returns {Promise<any | undefined>} - Task data or undefined if no task is selected
   */
  const { mutate, data: taskData } = useMutation({
    mutationFn: async (selected?: any): Promise<any> => {
      return selected
    },
  })

  // Active card
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null)

  const resetSelectedTask = () => {
    setActiveTaskId(null)
    mutate(undefined)
  }
  const refetchMyTask = () => refetch()

  // handle filter
  const handleFilter = (value: string) => {
    if (value === 'all') {
      navigate(`/my-task?processKey=&page=${page}`)
    } else {
      navigate(`/my-task?processKey=${value}&page=${page}`, { replace: true })
    }
  }

  // handle pagination
  const handlePagination = (pageToPaginanted: number) => {
    navigate(`/my-task?processKey=${processKey ?? ''}&page=${pageToPaginanted}`)
  }

  return (
    <Section title={processKey ? `My Tasks - ${selectedBusinessProcess?.label ?? ''}` : 'My Tasks'}>
      <div className="flex px-2 lg:px-6">
        <div className="block w-full sm:w-5/12 md:w-5/12 lg:w-4/12 2xl:w-3/12 sm:max-w-80 pr-0 sm:pr-2 pt-4">
          <div className="h-[calc(100vh-180px)] overflow-hidden sticky top-20 space-y-2.5">
            <div className="flex w-full justify-start items-center space-x-1 pb-2">
              <Select
                isLoading={isFetchingBusinessProcesses}
                options={businessProcessesOptions}
                onChange={e => {
                  handleFilter((e as { value: string }).value)
                  setActiveTaskId(null)
                  resetSelectedTask()
                }}
                defaultInputValue={processKey ?? undefined}
                isSearchable={true}
                placeholder="All Processes"
                className="max-w-64"
              />
              <div className="w-11">
                <div className="flex items-center">
                  <Button
                    icon={<RefreshCcw size={16} />}
                    onClick={() => {
                      refetch()
                      resetSelectedTask()
                    }}
                    isBlock={false}
                    size="small"
                    loading={isFetching}
                    data-testid="refresh-button"
                  />
                </div>
              </div>
            </div>
            {isFetching ? (
              <div className="flex items-center gap-2.5">
                <Spinner className="text-main-blue-alurkerja" />
                Getting My Tasks...
              </div>
            ) : (
              <div className="max-h-[calc(100vh-220px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-alurkerja-3 scrollbar-thumb-rounded-xl space-y-2 pb-12 sm:pb-4">
                {tasks && tasks.length > 0 ? (
                  <Fragment>
                    {tasks.map(task => (
                      <Fragment key={task.id}>
                        <CardTask
                          isActive={activeTaskId === task.id}
                          onClick={() => {
                            setActiveTaskId(task.id)
                            mutate(task)
                          }}
                          task={task}
                        />
                      </Fragment>
                    ))}
                    <div className="flex justify-center space-x-2 pb-4">
                      <Button
                        // variant="text"
                        icon={<ChevronLeft />}
                        onClick={() => handlePagination(Number(page) - 1)}
                        size="small"
                        disabled={Number(page) === 0}
                        data-testid="prev-button"
                      ></Button>
                      <Button
                        // variant="text"
                        icon={<ChevronRight />}
                        onClick={() => handlePagination(Number(page) + 1)}
                        size="small"
                        disabled={tasks && tasks.length < 10}
                        data-testid="next-button"
                      ></Button>
                    </div>
                  </Fragment>
                ) : (
                  <div className="flex flex-col items-center justify-items-center gap-2.5 border rounded-xl py-4 px-11">
                    <svg xmlns="http://www.w3.org/2000/svg" width="39" height="37" viewBox="0 0 39 37" fill="none">
                      <path
                        d="M0 33.3C0 34.2813 0.410892 35.2224 1.14228 35.9163C1.87368 36.6102 2.86566 37 3.9 37H9.75V33.3H3.9V27.75H0V33.3ZM39 33.3V27.75H35.1V33.3H29.25V37H35.1C36.1343 37 37.1263 36.6102 37.8577 35.9163C38.5891 35.2224 39 34.2813 39 33.3ZM35.1 3.7V9.25H39V3.7C39 2.7187 38.5891 1.77759 37.8577 1.08371C37.1263 0.38982 36.1343 0 35.1 0H29.25V3.7H35.1ZM0 3.7V9.25H3.9V3.7H9.75V0H3.9C2.86566 0 1.87368 0.38982 1.14228 1.08371C0.410892 1.77759 0 2.7187 0 3.7ZM15.6 37H23.4V33.3H15.6V37ZM15.6 3.7H23.4V0H15.6V3.7ZM35.1 22.2H39V14.8H35.1V22.2ZM0 22.2H3.9V14.8H0V22.2Z"
                        fill="#828282"
                      />
                    </svg>
                    <div className="text-xs">Belum ada Task untuk Kamu</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="w-0 overflow-hidden sm:w-7/12 md:w-7/12 lg:w-8/12 2xl:w-9/12 pl-0 sm:pl-2">
          {taskData ? (
            <MyTaskContextNew.Provider
              value={{
                data: taskData.id,
                processDefinition: taskData.process_definition_id!,
                refetchMyTask,
                resetSelectedTask,
                setActiveTaskId,
              }}
            >
              <TaskDetail></TaskDetail>
            </MyTaskContextNew.Provider>
          ) : (
            <Empty />
          )}
        </div>
      </div>
    </Section>
  )
}
